import type { AdminAction, AdminActivityEntry } from "../../api/admin";
import { Badge, FilterSelect, Pager, SearchBox } from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";

export interface ActivityFilterState {
  search: string;
  action: AdminAction | "";
}

type Tone = Parameters<typeof Badge>[0]["tone"];

/* How each action reads in the feed. Security-relevant ones (failed sign-in, resets) stand out. */
export const ACTION_LABEL: Record<AdminAction, [string, Tone]> = {
  signIn: ["Sign-in", "neutral"],
  signInFailed: ["Failed sign-in", "negative"],
  passwordChanged: ["Password changed", "info"],
  passwordResetRequested: ["Reset requested", "warning"],
  passwordReset: ["Password reset", "info"],
  passwordResetByAdmin: ["Reset by super admin", "warning"],
  adminInvited: ["Admin added", "accent"],
  dataExported: ["Export", "accent"],
  rosterImported: ["Roster upload", "accent"],
  documentViewed: ["Document viewed", "positive"],
  participantDeleted: ["Participant deleted", "negative"],
  gigSourcesRefreshed: ["Gig sources refreshed", "neutral"],
  gigFeedsChanged: ["Partner feeds changed", "accent"],
  gigPostsChanged: ["Gig posts changed", "accent"],
  winChecked: ["Win checked", "positive"],
  reminderTested: ["Test reminder", "info"],
};

/* An action newer than this build (the API was deployed first) still shows, under its own name, instead of crashing. */
const labelFor = (action: string): [string, Tone] =>
  ACTION_LABEL[action as AdminAction] ?? [action, "neutral"];

const FILTERS: [AdminAction, string][] = [
  ["signIn", "Sign-ins"],
  ["signInFailed", "Failed sign-ins"],
  ["dataExported", "Exports"],
  ["rosterImported", "Roster uploads"],
  ["documentViewed", "Document views"],
  ["passwordChanged", "Password changes"],
  ["passwordReset", "Password resets"],
  ["passwordResetRequested", "Reset requests"],
  ["passwordResetByAdmin", "Resets by super admin"],
  ["adminInvited", "Admins added"],
  ["participantDeleted", "Deletions"],
  ["gigSourcesRefreshed", "Gig source refreshes"],
  ["gigFeedsChanged", "Partner feed changes"],
  ["gigPostsChanged", "Gig post changes"],
  ["winChecked", "Wins checked"],
  ["reminderTested", "Test reminders"],
];

const when = (value: string) => {
  const date = new Date(value);
  const today = new Date().toDateString() === date.toDateString();
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return today
    ? `Today ${time}`
    : `${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} ${time}`;
};

interface ActivityScreenProps {
  rows: AdminActivityEntry[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  filter: ActivityFilterState;
  onFilter: (patch: Partial<ActivityFilterState>) => void;
  onPage: (page: number) => void;
}

/**
 * Everything admins have done, newest first, visible to every admin: sign-ins, password changes, exports, roster
 * uploads and each time someone opens a participant's CV or KYC screenshot. The server writes it; nobody can edit it.
 */
export function ActivityScreen({
  rows,
  total,
  loading,
  page,
  pageSize,
  filter,
  onFilter,
  onPage,
}: ActivityScreenProps) {
  const columns: Column<AdminActivityEntry>[] = [
    {
      key: "when",
      header: "When",
      width: "150px",
      render: (e) => (
        <span className="whitespace-nowrap text-text-muted tabular">{when(e.createdAt)}</span>
      ),
    },
    {
      key: "who",
      header: "Admin",
      render: (e) => (
        <div className="min-w-0">
          <div className="font-semibold">{e.actorName}</div>
          <div className="truncate text-[12px] text-text-muted">{e.actorEmail}</div>
        </div>
      ),
    },
    {
      key: "what",
      header: "What happened",
      render: (e) => {
        const [label, tone] = labelFor(e.action);
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={tone}>{label}</Badge>
            <span>{e.summary}</span>
            {e.targetLabel ? (
              <span className="text-[12px] text-text-muted">About: {e.targetLabel}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "ip",
      header: "From",
      hideBelow: "lg",
      render: (e) => <span className="text-[12px] text-text-muted tabular">{e.ip ?? ""}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-[13.5px] text-text-muted">
        Every admin sees this log. It is written by the server as things happen and can&apos;t be
        edited or cleared.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={filter.search}
          onChange={(search) => onFilter({ search })}
          placeholder="Search admin, participant or detail"
        />
        <FilterSelect
          label="Activity"
          value={filter.action}
          options={FILTERS}
          onChange={(action) => onFilter({ action })}
        />
      </div>
      <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
        <DataTable<AdminActivityEntry>
          rows={rows}
          columns={columns}
          rowKey={(e) => e.id}
          empty="Nothing matches these filters yet."
        />
      </div>
      <Pager page={page} total={total} size={pageSize} onPage={onPage} />
    </div>
  );
}
